/**
 * <TransferDialog /> — moves balance between two of the user's cash accounts.
 *
 * UX (iter-169zze refactor):
 * --------------------------
 *   • Inline validation per field (no more toast spam on submit).
 *   • Submit button auto-locks while the mutation is in flight
 *     (idempotent against double-click).
 *   • Backend ``detail`` codes are mapped to field-specific errors
 *     where applicable (e.g. ``transfer_source_and_destination_must_differ``
 *     lands on the ``destId`` field), with toast fallback for
 *     unmapped errors.
 *   • Mobile keyboards: amount + rate inputs get ``inputMode="decimal"``.
 *
 * Two selects (source + destination). Same id is rejected client-side
 * before the request to keep round-trip latency off the error path.
 * One amount input in the SOURCE account's currency. When source and
 * destination currencies differ, an FX block reveals itself with three
 * modes: market rate (default), manual rate, target destination amount.
 */
import { useEffect, useMemo, useState } from "react";
import { BottomSheet } from "./ui/bottom-sheet";
import { ArrowRightLeft, ArrowDown, ArrowUpRight, ArrowDownLeft } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatMoney } from "../lib/api";
import { pushHighlight } from "../lib/highlights";
import { useFormUX, FormField, SubmitButton } from "./form-ux";
import { MoneyAmountField } from "./MoneyAmountField";
import { AccountPickerField } from "./AccountPickerField";


/**
 * Map of backend ``detail`` strings → either a `{field, message}` pair
 * to land on a specific input, or just a message to toast.
 *
 * Keeping the table at module scope makes it easy to extend when the
 * backend grows new error codes.
 */
function buildErrorMap(isEs) {
  return {
    transfer_requires_source_and_destination_accounts: {
      field: "sourceId",
      message: isEs ? "Faltan cuenta origen o destino." : "Missing source or destination.",
    },
    transfer_source_and_destination_must_differ: {
      field: "destId",
      message: isEs ? "Origen y destino deben ser distintos." : "Source and destination must differ.",
    },
    transfer_amount_must_be_positive: {
      field: "amount",
      message: isEs ? "El monto debe ser mayor a 0." : "Amount must be positive.",
    },
    transfer_market_rate_unavailable_provide_manual: {
      field: "manualRate",
      message: isEs
        ? "Sin tasa de mercado disponible — ingresa la tasa manual."
        : "No market rate available — enter a manual rate.",
    },
    transfer_account_not_found: {
      field: null,
      message: isEs ? "Cuenta no encontrada." : "Account not found.",
    },
  };
}


export default function TransferDialog({
  accounts,
  isEs,
  trigger,
  open,
  onOpenChange,
  defaultSourceId = "",
}) {
  const qc = useQueryClient();
  // Controlled / uncontrolled open prop — use parent's state if
  // provided, otherwise keep our own.
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = open ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;

  const initial = useMemo(() => ({
    sourceId: defaultSourceId || "",
    destId: "",
    amount: "",
    description: "",
    overrideMode: "market",   // "market" | "rate" | "amount"
    toAmount: "",
    manualRate: "",
  }), [defaultSourceId]);

  // iter-169zze — Centralised state with field-level validation.
  const form = useFormUX(initial, {
    validate: (v) => {
      const fieldErrors = {};
      if (!v.sourceId) {
        fieldErrors.sourceId = isEs ? "Elige la cuenta de origen." : "Pick a source account.";
      }
      if (!v.destId) {
        fieldErrors.destId = isEs ? "Elige la cuenta de destino." : "Pick a destination account.";
      } else if (v.sourceId && v.destId === v.sourceId) {
        fieldErrors.destId = isEs ? "Debe ser distinta a la origen." : "Must differ from source.";
      }
      const amt = parseFloat(v.amount);
      if (!amt || amt <= 0 || Number.isNaN(amt)) {
        fieldErrors.amount = isEs ? "Monto inválido. Debe ser un número mayor a 0." : "Invalid amount. Must be a number greater than 0.";
      }
      // Cross-currency manual modes need their own field to be valid.
      const src = accounts.find((a) => a.id === v.sourceId);
      const dst = accounts.find((a) => a.id === v.destId);
      const cross = !!(src && dst && src.currency !== dst.currency);
      if (cross) {
        if (v.overrideMode === "rate") {
          const r = parseFloat(v.manualRate);
          if (!r || r <= 0) {
            fieldErrors.manualRate = isEs ? "Tasa inválida." : "Invalid rate.";
          }
        } else if (v.overrideMode === "amount") {
          const t = parseFloat(v.toAmount);
          if (!t || t <= 0) {
            fieldErrors.toAmount = isEs ? "Monto destino inválido." : "Invalid destination amount.";
          }
        }
      }
      return { fieldErrors };
    },
  });

  // Reset all FX-specific state when accounts change so we don't leak a
  // stale manual rate into a same-currency transfer.
  useEffect(() => {
    form.setValue("overrideMode", "market");
    form.setValue("toAmount", "");
    form.setValue("manualRate", "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.values.sourceId, form.values.destId]);

  // Reset the whole form when the dialog reopens.
  useEffect(() => {
    if (isOpen) form.reset(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const source = useMemo(
    () => accounts.find((a) => a.id === form.values.sourceId),
    [accounts, form.values.sourceId],
  );
  const dest = useMemo(
    () => accounts.find((a) => a.id === form.values.destId),
    [accounts, form.values.destId],
  );
  const crossCurrency = !!(source && dest && source.currency !== dest.currency);

  // ── Live preview of converted amount when accounts + mode are set ──
  const overrideRate = form.values.overrideMode === "rate" && parseFloat(form.values.manualRate) > 0
    ? parseFloat(form.values.manualRate) : null;
  const overrideToAmount = form.values.overrideMode === "amount" && parseFloat(form.values.toAmount) > 0
    ? parseFloat(form.values.toAmount) : null;
  const computedDest = (() => {
    const amt = parseFloat(form.values.amount) || 0;
    if (!crossCurrency) return amt;
    if (overrideToAmount) return overrideToAmount;
    if (overrideRate) return amt * overrideRate;
    return null;
  })();
  const computedRate = (() => {
    if (!crossCurrency) return 1;
    if (overrideRate) return overrideRate;
    if (overrideToAmount && parseFloat(form.values.amount) > 0) {
      return overrideToAmount / parseFloat(form.values.amount);
    }
    return null;
  })();

  // ── Submit handler with backend-error mapping ──
  const errorMap = buildErrorMap(isEs);

  const onSubmit = form.handleSubmit(
    async (v) => {
      const body = {
        type: "transfer",
        amount: parseFloat(v.amount),
        account_id: v.sourceId,
        to_account_id: v.destId,
        description: v.description || (isEs ? "Traslado entre cuentas" : "Account transfer"),
      };
      if (crossCurrency && v.overrideMode === "amount" && parseFloat(v.toAmount) > 0) {
        body.to_amount = parseFloat(v.toAmount);
      } else if (crossCurrency && v.overrideMode === "rate" && parseFloat(v.manualRate) > 0) {
        body.fx_rate_used = parseFloat(v.manualRate);
      }
      const doc = (await api.post("/movements/transfer", body)).data;
      qc.invalidateQueries({ queryKey: ["cash-accounts"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      qc.invalidateQueries({ queryKey: ["movements"] });
      qc.invalidateQueries({ queryKey: ["history"] });
      qc.invalidateQueries({ queryKey: ["movements-infinite"] });
      pushHighlight("movement", doc?.id);
      toast.success(isEs ? "Traslado registrado" : "Transfer recorded");
      setOpen(false);
    },
    {
      // iter-169zze — backend detail strings → inline field errors.
      onError: ({ message, err }) => {
        const detail = err?.response?.data?.detail;
        if (typeof detail === "string" && errorMap[detail]) {
          const { field, message: m } = errorMap[detail];
          if (field) {
            form.setError(field, m);
            // For "no market rate" backend hint we also flip the mode
            // so the manual-rate field appears AND is in focus.
            if (detail === "transfer_market_rate_unavailable_provide_manual") {
              form.setValue("overrideMode", "rate");
            }
          } else {
            toast.error(m);
          }
          return;
        }
        toast.error(message || (isEs ? "No se pudo trasladar." : "Transfer failed."));
      },
    },
  );

  return (
    <>
      {trigger && (
        <span
          onClick={() => setOpen(true)}
          className="inline-flex"
          data-testid="transfer-dialog-trigger"
        >
          {trigger}
        </span>
      )}
      <BottomSheet
        open={isOpen}
        onOpenChange={setOpen}
        testid="transfer-dialog"
        size="md"
        title={(
          <span className="inline-flex items-center gap-2">
            <ArrowRightLeft className="h-4 w-4 text-emerald-700" />
            {isEs ? "Traslado entre cuentas" : "Account transfer"}
          </span>
        )}
        description={isEs
          ? "Mueve saldo de una cuenta tuya a otra. Si tienen distinta moneda aplicamos la tasa del mercado (o puedes definirla)."
          : "Move balance between your own accounts. Cross-currency transfers use the market rate (or yours)."}
        footer={(
          <SubmitButton
            form="transfer-form"
            submitting={form.submitting}
            loadingText={isEs ? "Trasladando…" : "Transferring…"}
            testid="transfer-submit"
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            <ArrowRightLeft className="h-4 w-4" />
            {isEs ? "Trasladar" : "Transfer"}
          </SubmitButton>
        )}
      >
        <form
          id="transfer-form"
          onSubmit={onSubmit}
          className="space-y-3"
          data-testid="transfer-form"
          noValidate
        >
          <div className="grid grid-cols-1 gap-3">
            {/* iter-188 — Explicit "qué sale" label with red tone +
                outgoing arrow. Replaces the blander "Desde". */}
            <div data-testid="transfer-source-block">
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-red-100 text-red-700">
                  <ArrowUpRight className="h-3 w-3" />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-wider text-red-700">
                  {isEs ? "Sale de" : "Out of"}
                </span>
              </div>
              <AccountPickerField
                label=""
                name="sourceId"
                required
                isEs={isEs}
                accounts={accounts}
                value={form.values.sourceId}
                onChange={(v) => { form.setValue("sourceId", v); form.markTouched("sourceId"); }}
                onBlur={() => form.markTouched("sourceId")}
                placeholder={isEs ? "Cuenta origen" : "Source account"}
                error={form.touched.sourceId || form.submitCount > 0 ? form.errors.sourceId : undefined}
                testid="form-field-sourceId"
              />
            </div>

            <div className="flex justify-center" aria-hidden="true">
              <div className="h-7 w-7 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center">
                <ArrowDown className="h-3.5 w-3.5 text-slate-500" />
              </div>
            </div>

            <div data-testid="transfer-dest-block">
              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-emerald-100 text-emerald-700">
                  <ArrowDownLeft className="h-3 w-3" />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                  {isEs ? "Entra en" : "Into"}
                </span>
              </div>
              <AccountPickerField
                label=""
                name="destId"
                required
                isEs={isEs}
                accounts={accounts}
                value={form.values.destId}
                onChange={(v) => { form.setValue("destId", v); form.markTouched("destId"); }}
                onBlur={() => form.markTouched("destId")}
                placeholder={isEs ? "Cuenta destino" : "Destination account"}
                excludeIds={[form.values.sourceId]}
                error={form.touched.destId || form.submitCount > 0 ? form.errors.destId : undefined}
                testid="form-field-destId"
              />
            </div>
          </div>

          <MoneyAmountField
            label={isEs ? "Monto" : "Amount"}
            name="amount"
            required
            currency={source?.currency}
            primaryCurrency={source?.currency === "USD" ? "ARS" : "USD"}
            balance={source?.balance}
            balanceWarningOnly={false}
            value={form.values.amount}
            onChange={(v) => form.setValue("amount", v)}
            onBlur={() => form.markTouched("amount")}
            error={form.touched.amount || form.submitCount > 0 ? form.errors.amount : undefined}
            inputProps={{ "data-testid": "transfer-amount" }}
          />

          <FormField
            label={isEs ? "Descripción (opcional)" : "Description (optional)"}
            name="description"
          >
            <FormField.Input
              placeholder={isEs ? "Ej. Pago a tarjeta, ahorro mensual…" : "e.g. Card payment, monthly savings…"}
              autoComplete="off"
              data-testid="transfer-description"
              {...form.getFieldProps("description")}
            />
          </FormField>

          {crossCurrency && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2" data-testid="transfer-fx-block">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs font-semibold text-amber-900">
                  {isEs
                    ? `Conversión ${source?.currency} → ${dest?.currency}`
                    : `Conversion ${source?.currency} → ${dest?.currency}`}
                </div>
                <select /* design-system: ALLOW legacy-todo */
                  value={form.values.overrideMode}
                  onChange={(e) => form.setValue("overrideMode", e.target.value)}
                  data-testid="transfer-fx-mode"
                  className="text-[11px] rounded-md border border-amber-300 bg-white px-1.5 py-0.5"
                >
                  <option value="market">{isEs ? "Tasa de mercado" : "Market rate"}</option>
                  <option value="rate">{isEs ? "Tasa manual" : "Manual rate"}</option>
                  <option value="amount">{isEs ? "Definir monto destino" : "Set destination amount"}</option>
                </select>
              </div>

              {form.values.overrideMode === "rate" && (
                <FormField
                  label={isEs
                    ? `Tasa (1 ${source?.currency} = ? ${dest?.currency})`
                    : `Rate (1 ${source?.currency} = ? ${dest?.currency})`}
                  name="manualRate"
                  required
                  error={form.touched.manualRate || form.submitCount > 0 ? form.errors.manualRate : undefined}
                >
                  <FormField.Currency
                    placeholder="0.00025"
                    data-testid="transfer-fx-manual-rate"
                    value={form.values.manualRate}
                    onChange={(e) => form.setValue("manualRate", e.target.value)}
                    onBlur={() => form.markTouched("manualRate")}
                  />
                </FormField>
              )}
              {form.values.overrideMode === "amount" && (
                <FormField
                  label={isEs ? `Monto destino (${dest?.currency})` : `Destination amount (${dest?.currency})`}
                  name="toAmount"
                  required
                  error={form.touched.toAmount || form.submitCount > 0 ? form.errors.toAmount : undefined}
                >
                  <FormField.Currency
                    currency={dest?.currency}
                    placeholder="0.00"
                    data-testid="transfer-fx-target-amount"
                    value={form.values.toAmount}
                    onChange={(e) => form.setValue("toAmount", e.target.value)}
                    onBlur={() => form.markTouched("toAmount")}
                  />
                </FormField>
              )}

              <div className="text-[11px] text-amber-900 mt-1" data-testid="transfer-fx-preview">
                {computedRate !== null && computedDest !== null ? (
                  <>
                    {isEs ? "Recibirás" : "You'll receive"}{" "}
                    <span className="font-semibold">{formatMoney(computedDest, dest?.currency)}</span>{" "}
                    · {isEs ? "tasa" : "rate"}{" "}
                    <span className="font-mono">{computedRate.toFixed(6)}</span>
                  </>
                ) : (
                  <>
                    {isEs
                      ? "Aplicaremos la tasa del mercado al confirmar."
                      : "Market rate will be applied on confirm."}
                  </>
                )}
              </div>
            </div>
          )}
          {/* iter-188 — Impact preview. Shows the user EXACTLY what
              the two accounts will look like AFTER the transfer
              clears, so there's no "did I pick the right source?"
              ambiguity. Only renders once a positive amount and both
              accounts are chosen. */}
          {(() => {
            const amt = parseFloat(form.values.amount);
            if (!source || !dest || !amt || amt <= 0) return null;
            const destDelta = (crossCurrency && computedDest !== null) ? computedDest : amt;
            return (
              <div
                data-testid="transfer-impact-preview"
                className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3 text-xs"
              >
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">
                  {isEs ? "Vista previa" : "Preview"}
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-slate-700">
                      <ArrowUpRight className="h-3 w-3 text-red-600 shrink-0" />
                      <span className="truncate">{source.name}</span>
                    </span>
                    <span
                      data-testid="transfer-impact-source"
                      className="tabular-nums font-mono text-red-700 font-semibold whitespace-nowrap"
                    >
                      −{formatMoney(amt, source.currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 pl-3 text-[10px] text-slate-400">
                    <span>{isEs ? "saldo después" : "balance after"}</span>
                    <span className="tabular-nums font-mono">
                      {formatMoney((source.balance || 0) - amt, source.currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-slate-100">
                    <span className="flex items-center gap-1.5 text-slate-700">
                      <ArrowDownLeft className="h-3 w-3 text-emerald-600 shrink-0" />
                      <span className="truncate">{dest.name}</span>
                    </span>
                    <span
                      data-testid="transfer-impact-dest"
                      className="tabular-nums font-mono text-emerald-700 font-semibold whitespace-nowrap"
                    >
                      +{formatMoney(destDelta, dest.currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 pl-3 text-[10px] text-slate-400">
                    <span>{isEs ? "saldo después" : "balance after"}</span>
                    <span className="tabular-nums font-mono">
                      {formatMoney((dest.balance || 0) + destDelta, dest.currency)}
                    </span>
                  </div>
                </div>
              </div>
            );
          })()}
        </form>
      </BottomSheet>
    </>
  );
}


// (Uncontrolled-vs-controlled open prop handled inline at the top of
// the component — no external helper needed.)
